/**
 * Реєстр видів документів. Один опис на вид: що він вимагає, звідки береться
 * бланк, які підстановки в ньому доступні. Веб малює список із цього реєстру,
 * тому новий документ не додає жодної сторінки налаштувань.
 */
/** Загальні для всіх документів: людина, установа, посадовці, дати. */
const COMMON = [
    ['{NAME_DAT}', 'ПІБ у давальному'],
    ['{NAME_NOM}', 'Ім’я ПРІЗВИЩЕ для підпису'],
    ['{NAME_GEN}', 'ПРІЗВИЩЕ І.П. у родовому'],
    ['{RANK}', 'звання у називному'],
    ['{RANK_DAT}', 'звання у давальному'],
    ['{RANK_GEN}', 'звання у родовому'],
    ['{ROLE}', 'посада («Курсант»)'],
    ['{ROLE_DAT}', 'посада у давальному'],
    ['{UNIT}', 'підрозділ'],
    ['{UNIT_PHRASE}', 'підрозділ зі словом («С-21 групи »); у старшини курсу — порожньо'],
    ['{COURSE}', 'курс із налаштувань'],
    ['{INSTITUTION}', 'назва установи'],
    ['{INSTITUTION_DAT}', 'установа у давальному'],
    ['{CHIEF_ADDRESSEE}', 'звернення до начальника установи («Т.в.о. начальника ІСЗЗІ КПІ»)'],
    ['{CHIEF_ADDRESSEE_2}', 'другий рядок звернення: «ім. Ігоря Сікорського»'],
    ['{NAME_SHORT}', 'Прізвище І.П.'],
    ['{RANK_SHORT}', 'звання скорочено'],
    ['{NAME_GEN_DOT}', 'ПРІЗВИЩЕ І.П. у родовому з крапкою'],
    ['{TAX_CODE}', 'ідентифікаційний код'],
    ['{CITY}', 'місто установи'],
    ['{ORG}', 'коротка назва курсу'],
    ['{CLERK}', 'виконавець'],
    ['{CLERK_PHONE}', 'телефон виконавця'],
    ['{DATE}', 'сьогоднішня дата'],
    ['{FROM}', 'початок періоду'],
    ['{TO}', 'кінець періоду'],
    ['{YEAR}', 'рік періоду'],
];
/** Посадовці: у кожної групи однаковий набір ключів. */
const OFFICIALS = [
    ['CHIEF', 'начальник'],
    ['DEPUTY', 'заступник'],
    ['COURSE_HEAD', 'курсовий офіцер'],
    ['MEDIC', 'медслужба'],
].flatMap(([key, who]) => [
    [`{${key}_NAME}`, `${who}: ім'я та прізвище`],
    [`{${key}_NAME_SHORT}`, `${who}: прізвище з ініціалами`],
    [`{${key}_NAME_DAT}`, `${who}: ПРІЗВИЩЕ І.П. для звертання`],
    [`{${key}_INITIALS}`, `${who}: ініціали`],
    [`{${key}_RANK}`, `${who}: звання`],
    [`{${key}_POSITION}`, `${who}: посада`],
    [`{${key}_POSITION_DAT}`, `${who}: посада у давальному`],
    [`{${key}_GEN}`, `${who}: прізвище у родовому`],
    [`{${key}_GEN_DOT}`, `${who}: прізвище у родовому з крапкою`],
    [`{${key}_NOTE}`, `${who}: примітка під посадою`],
]);
/**
 * Бланки звільнення: і на кілька годин, і на добу. Ключі в них однакові —
 * різниця лише в тому, звідки береться час і підстава, тож і список один.
 */
const LEAVE = [
    ['{LEAVE_FROM}', 'початок звільнення: час і дата'],
    ['{LEAVE_TO}', 'кінець звільнення: час і дата'],
    ['{LEAVE_REASON}', 'підстава («у зв’язку з сімейними обставинами»)'],
    ['{LEAVE_ADDRESS}', 'адреса, за якою проводиться звільнення'],
    ['{LEAVE_PHONE}', 'телефон для зв’язку'],
    ['{SHELTER_ADDRESS}', 'адреса укриття на випадок тривоги'],
    ['{SQUAD}', 'відділення («I відділення»)'],
    ...['SQUAD_LEAD', 'GROUP_LEAD'].flatMap(key => {
        const who = key === 'SQUAD_LEAD' ? 'командир відділення' : 'командир групи';
        return [
            [`{${key}_RANK}`, `${who}: звання`],
            [`{${key}_RANK_GEN}`, `${who}: звання у родовому`],
            [`{${key}_NAME}`, `${who}: ім’я та прізвище`],
            [`{${key}_GEN}`, `${who}: прізвище у родовому`],
        ];
    }),
];
export const DOC_KINDS = [
    {
        id: 'report',
        label: 'Рапорт на компенсацію',
        hint: 'Особистий рапорт військовослужбовця з ходатайствами й погодженням.',
        needs: ['person', 'period', 'officials'],
        output: 'docx',
        feature: 'reports',
        fallback: 'none',
        // Порядок бланка: резолюція начальника вгорі, під нею рапорт курсанта
        // начальникові курсу, клопотання курсу — заступникові, клопотання
        // заступника — начальникові, і аж унизу «ПОГОДЖЕНО» медслужби.
        // Ledger — підпис лише під місячною відомістю, у рапорті на харчування
        // його місця немає.
        signedBy: ['chief', 'courseHead', 'deputy', 'medic'],
        placeholders: [
            ['{MEALS}', 'перелік прийомів їжі за період'],
            ['{BODY}', 'готове тіло рапорту (текст із налаштувань)'],
            ...COMMON,
            ...OFFICIALS,
        ],
    },
    {
        id: 'leaveReport',
        label: 'Рапорт на звільнення',
        hint: 'Рапорт про звільнення з розташування на визначений час із ланцюжком клопотань.',
        needs: ['person', 'date', 'officials'],
        output: 'docx',
        feature: 'reports',
        fallback: 'none',
        // Ланцюжок клопотань: курсовий офіцер (начальник курсу), заступник і
        // начальник Інституту, якому адресована остання ланка. Начальника теж
        // обирають: у бланку він набраний текстом, і без вибору звернення завжди
        // лишалося на першому зі списку — «Начальник» там, де мав стояти «Т.в.о.».
        // Медслужба цей рапорт не погоджує, тож її в переліку немає.
        signedBy: ['courseHead', 'deputy', 'chief'],
        placeholders: [...LEAVE, ...COMMON, ...OFFICIALS],
    },
    {
        id: 'dutyReport',
        label: 'Рапорт до ранку',
        hint: 'Звільнення на добу: з вечора до ранку наступного дня, з ланцюжком клопотань.',
        needs: ['person', 'date', 'officials'],
        output: 'docx',
        feature: 'reports',
        fallback: 'none',
        // Підписи ті самі, що й у звільненні: начальник курсу, заступник і
        // начальник, якому адресована остання ланка ланцюжка.
        signedBy: ['courseHead', 'deputy', 'chief'],
        // Бланк той самий, що й на звільнення, тож і ключі однакові: різниця в
        // тому, що час, підставу, адресу й телефон програма підставляє сама.
        placeholders: [...LEAVE, ...COMMON, ...OFFICIALS],
    },
    {
        id: 'bonusReport',
        label: 'Рапорт на виплату',
        hint: 'Виплата винагороди за місяць: перелік складається з таблиці, окремо — ті, у кого стягнення.',
        needs: ['period', 'officials'],
        output: 'docx',
        feature: 'reports',
        fallback: 'none',
        // Порядок бланка: резолюція начальника вгорі, рапорт адресовано першому
        // заступникові, підписує його начальник курсу, і вже під клопотанням
        // заступника йдуть «ПОГОДЖЕНО» — серед них медслужба.
        signedBy: ['chief', 'deputy', 'courseHead', 'medic'],
        placeholders: [
            ['{PEOPLE}', 'перелік тих, кому виплатити (абзац замінюється рядками)'],
            ['{PENALIZED}', 'перелік тих, кому не виплачувати через стягнення'],
            ['{PENALIZED_INTRO}', 'позначка вступу до пункту 2: зникає разом із пунктом, якщо стягнень немає'],
            ['{AMOUNT}', 'розмір винагороди з налаштувань'],
            ['{MONTH_YEAR}', 'місяць і рік періоду: «травень 2026»'],
            ['{PERIOD_PHRASE}', '«з 01 травня по 31 травня 2026 року»'],
            ...COMMON,
            ...OFFICIALS,
        ],
    },
    {
        id: 'leaveList',
        label: 'Список звільнення',
        hint: 'Хто звільняється з розташування на конкретну дату, з періодами звільнення.',
        needs: ['date'],
        output: 'docx',
        fallback: 'none',
        // Рядки списку бот складає сам за таблицею, тому підстановок у бланку
        // майже немає: дата в заголовку і рядки замість зразкових.
        placeholders: [['{DATE}', 'дата списку']],
    },
    {
        id: 'overnightList',
        label: 'Іногородні до ранку',
        hint: 'Не кияни, яких список звільнення відпускає до ранку: де перебувають і як зв’язатися.',
        needs: ['date'],
        output: 'docx',
        fallback: 'none',
        // Підстановок немає: рядок дат і рядки людей заповнюються за місцем у
        // таблиці бланка, а рядок курсу лишається таким, як у бланку.
        placeholders: [],
    },
    {
        id: 'canteenList',
        label: 'Список ПЗ',
        hint: 'Хто фактично харчується в їдальні на конкретну дату.',
        needs: ['date'],
        output: 'xlsx',
        feature: 'canteenList',
        fallback: 'none',
        placeholders: [
            ['{COURSE}', 'курс із налаштувань'],
            ['{DATE}', 'дата списку'],
            ['{INSTITUTION}', 'назва установи'],
        ],
    },
    {
        id: 'monthTable',
        label: 'Місячна відомість',
        hint: 'Таблиця позначок за місяць із підписами під нею.',
        needs: ['period'],
        output: 'xlsx',
        fallback: 'none',
        placeholders: [
            ['{COURSE}', 'курс із налаштувань'],
            ['{INSTITUTION}', 'назва установи'],
            ['{YEAR}', 'рік періоду'],
        ],
    },
];
export const docKind = (id) => DOC_KINDS.find(k => k.id === id);
/**
 * Види, доступні цій організації: вимкнений розділ ховає і його документи.
 * Оголошені, але ще не готові види сюди не потрапляють — бланк їм завантажити
 * нікуди. Їх показує список документів курсу, де видно, що заплановано.
 */
export const availableDocKinds = (features) => DOC_KINDS.filter(k => (!k.feature || features[k.feature]) && !k.planned);
//# sourceMappingURL=doc-kinds.js.map